import { useState } from 'react';
import { Navigate, Outlet, Route, useLocation } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ToolOutlined } from '@ant-design/icons';
import { Typography } from 'antd';

import ApplicationLayout from 'layouts/ApplicationLayout/ApplicationLayout';
import AdminPortalLayout from 'layouts/AdminPortalLayout/AdminPortalLayout';
import CustomerLayout from 'layouts/CustomerLayout/CustomerLayout';

import NoteBox from 'components/NoteBox/NoteBox';
import CustomerDirectLinkSearch from 'components/CustomerDirectLinkSearch/CustomerDirectLinkSearch';
import SectionMessage from 'components/SectionMessage/SectionMessage';

import CustomerSearchPage from 'pages/CustomerSearchPage/CustomerSearchPage';
import ApplicationSearchPage from 'pages/ApplicationSearchPage/ApplicationSearchPage';
import FraudFilesSearchPage from 'pages/FraudFilesSearchPage/FraudFilesSearchPage';
import NotFoundPage from 'pages/NotFoundPage/NotFoundPage';
import ThemeTestPage from 'pages/ThemeTestPage/ThemeTestPage';
import BlockPhonePage from 'pages/BlockPhonePage/BlockPhonePage';
import BulkOperationsPage from 'pages/BulkOperationsPage/BulkOperationsPage';
import { SIZE } from 'utilities/constants';

import { SentryRoutes } from 'services/sentry';
import { styledWithTransientProps } from 'utilities/styles';

import Header from './Header';
import Softphone from './Softphone';
import Sidebar, { SidebarProvider, useSidebarHasContent } from './Sidebar';

const Grid = styledWithTransientProps('div')(({
  $isSearchPage,
  $sidebarHasContent,
  $softphoneOpen,
}) => {
  const contentBackgroundColor = $isSearchPage
    ? 'var(--color-background)'
    : 'var(--neutral-1)';

  const sidebarWidth = $sidebarHasContent ? 'var(--sidebar-width)' : '0px';
  const softphoneWidth = $softphoneOpen ? 'var(--softphone-width)' : '0px';

  return `
      height: 100vh;
      display: grid;
      grid-template-areas:
        "header header header"
        "sidebar content softphone";
      grid-template-columns: ${sidebarWidth} 1fr ${softphoneWidth};
      grid-template-rows: var(--header-height) 1fr;
      transition: all .3s ease;

      .header-container {
        grid-area: header;
        z-index: 1;
      }

      .content-container {
        background-color: ${contentBackgroundColor};
        grid-area: content;
        overflow-y: scroll;
        padding: var(--spacing-sm);
      }

      .sidebar-container {
        grid-area: sidebar;
        background-color: var(--color-background);
        border-right: var(--border-secondary);
        overflow-x: hidden;
      }

      .softphone-container {
        grid-area: softphone;
        background-color: var(--color-background);
        border-left: var(--border-secondary);
        overflow-x: hidden;
      }
    `;
});

const DashboardLayout = () => {
  const sidebarHasContent = useSidebarHasContent();
  const [softphoneOpen, setSoftphoneOpen] = useState(false);
  const { showAdminPortal } = useFlags();

  const toggleSoftphoneOpen = () => {
    setSoftphoneOpen((previousValue) => !previousValue);
  };

  // Temporary.
  // Account/application pages would need some layout changes befor things would look good.
  // so it's easiest to just keep the background grey and make limited changes.
  // Matches /customer-search, /application-search, /fraud-files-search, etc.
  // react-router patterns can't express a wildcard mid-segment, so test the pathname directly.
  const { pathname } = useLocation();
  const isSearchPage = /^\/[^/]*-search(\/|$)/.test(pathname);

  return (
    <SidebarProvider>
      <Grid
        $isSearchPage={isSearchPage}
        $sidebarHasContent={sidebarHasContent}
        $softphoneOpen={softphoneOpen}
      >
        <div className="header-container">
          <Header
            softphoneOpen={softphoneOpen}
            toggleSoftphoneOpen={toggleSoftphoneOpen}
          />
        </div>

        <div className="sidebar-container">
          <Sidebar />
        </div>

        <div className="softphone-container">
          <Softphone />
        </div>

        <div className="content-container">
          <SentryRoutes>
            <Route path="/" element={<Navigate replace to="/customers" />} />
            <Route
              path="/customers"
              element={<Navigate replace to="/customer-search" />}
            />
            <Route
              path="/applications"
              element={<Navigate replace to="/application-search" />}
            />

            <Route path="/customer-search" element={<CustomerSearchPage />} />

            <Route
              path="/application-search"
              element={<ApplicationSearchPage />}
            />

            <Route
              path="/fraud-files-search"
              element={<FraudFilesSearchPage />}
            />

            <Route
              path="/search-email"
              element={<CustomerDirectLinkSearch />}
            />

            {showAdminPortal ? (
              <Route
                path="/admin"
                element={
                  <AdminPortalLayout>
                    <Outlet />
                  </AdminPortalLayout>
                }
              >
                <Route
                  index
                  element={
                    <SectionMessage
                      height="500px"
                      icon={ToolOutlined}
                      text="Admin Tools"
                      size={SIZE.MD}
                      body={
                        <Typography.Paragraph type="secondary" align="center">
                          Select a tool from the sidebar to get started
                        </Typography.Paragraph>
                      }
                    />
                  }
                />
                <Route path="block-phone/*" element={<BlockPhonePage />} />
                <Route
                  path="bulk-operations/*"
                  element={<BulkOperationsPage />}
                />
              </Route>
            ) : (
              <Route
                path="/admin/*"
                element={<Navigate replace to="/customer-search" />}
              />
            )}

            <Route
              path="/applications/:applicationId/*"
              element={<ApplicationLayout />}
            />

            <Route
              path="/customers/:customerId/*"
              element={<CustomerLayout />}
            />

            <Route path="/themetest/*" element={<ThemeTestPage />} />

            <Route path="*" element={<NotFoundPage />} />
          </SentryRoutes>
        </div>
      </Grid>
      <NoteBox />
    </SidebarProvider>
  );
};

export default DashboardLayout;
